import { call, put, select } from 'redux-saga/effects';
import { ApiError, ApiResponse } from 'store/api/types';
import { logoutRequest } from 'store/auth/actions';
import { turnMessageOn } from 'store/app/actions';
import { DataLogStatus, SubmissionUpdateField } from 'store/app/enums';
import { MessageData } from 'store/app/types';
import { MessageStates } from 'containers/MessageBox';
import { updateSubmissionLog } from 'store/organisation/actions';
import {
  changeStatusAfterUpdate,
  sendClientApprovalFailure,
  sendClientApprovalSuccess,
} from 'store/vat/actions';
import { SendClientApprovalRequestAction } from 'store/vat/types';
import { checkResponse } from 'store/utils';
import { isTifdReportPath } from 'utils/tifd-mapping';
import { renderErrorMessage } from 'utils/render-error-message';
import i18n from 'i18next';
import { sendTifdToClient, TifdStatusRequest } from '../api';
import { getTifdContext, getTifdReport } from '../selectors';

/**
 * Send for client approval from the TIFD report page. The drawer still
 * collects the email. Returns true when this request belonged to TIFD,
 * including when the call failed. VAT send-for-client continues only when
 * this returns false.
 */
/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* sendTifdToClientFromReturnSaga(
  action: SendClientApprovalRequestAction
) {
  if (!isTifdReportPath(window.location.pathname)) return false;

  const { dataLogId, emailData, mappingId } = action.payload;
  try {
    const context: ReturnType<typeof getTifdContext> = yield select(
      getTifdContext
    );
    const report: ReturnType<typeof getTifdReport> = yield select(getTifdReport);
    const applicationConnectionId = context?.applicationConnectionId;
    const reportId = report?.id || dataLogId;
    if (!applicationConnectionId || !reportId) {
      throw new Error('TIFD report is not ready to send to the client.');
    }

    const body: TifdStatusRequest = {
      reportUrl: emailData.emailUrl,
      emailUrl: emailData.emailUrl,
      emailSubject: emailData.emailSubject,
      emailMessage: emailData.emailMessage,
      note: emailData.note || undefined,
      includePreparer: emailData.isPreparerIncluded,
      includeReviewer: emailData.isReviewerIncluded,
    };

    const response: ApiResponse = yield call(
      sendTifdToClient,
      applicationConnectionId,
      reportId,
      body
    );

    const err: ApiError | null = checkResponse(response?.responseMessage);
    if (err) throw err;

    const message: MessageData = {
      title: i18n.t('common.success-exclamation'),
      description: response.responseMessage.message,
      type: MessageStates.SUCCESS,
    };

    yield put(turnMessageOn(message));
    yield put(
      changeStatusAfterUpdate(DataLogStatus.AWAITING_CLIENT_APPROVAL)
    );
    yield put(
      updateSubmissionLog(
        mappingId,
        SubmissionUpdateField.STATUS,
        'AWAITING_CLIENT_APPROVAL',
        dataLogId
      )
    );
    yield put(sendClientApprovalSuccess());
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(sendClientApprovalFailure());
  }

  return true;
}
